import {SQS} from "aws-sdk";
import {Environment, LoggingConfig, SqsConfig} from "./config";
import {R4ServerData} from "./ServerDataTypes";
import {Logging, RollbarLogging, StubLogging} from "./Logging";
import {R4Message} from "./R4Message";

export interface Sqs {
  processOneMessage(handler: (message) => Promise<void>): Promise<void>;
}

export class StubSqs implements Sqs {

  constructor(){}

  private messageQueue: object[] = [];

  async processOneMessage(handler: (message) => Promise<void>): Promise<void> {
    if (this.messageQueue.length > 0) {
      await handler(this.messageQueue[0]);
      this.messageQueue = this.messageQueue.slice(1);
    } else return
  }

  enqueueMessage(message: object): void {
    this.messageQueue.push(message);
  }
}

export class RealSqs implements Sqs {

  private readonly sqs: SQS;

  constructor(
    private readonly licenceKeyData: R4ServerData
  ) {
    this.sqs = new SQS({
      region: licenceKeyData.sqsRegion,
      credentials: {
        accessKeyId: licenceKeyData.sqsCredentials.AccessKeyId,
        secretAccessKey: licenceKeyData.sqsCredentials.SecretAccessKey,
        sessionToken: licenceKeyData.sqsCredentials.SessionToken
      }
    });
  }

  /*
  A very simple message processing loop designed on the premise that the worst case scenario is
  not very bad: the user is shown a warning saying that a patient's data couldn't be automatically
  synced, and they have to enter it manually.
   */
  async processOneMessage(messageHandler: (message: R4Message) => Promise<void>): Promise<void> {
    const queueUrl = this.licenceKeyData.sqsQueueUrl;
    const sqs = this.sqs;

    await new Promise((res, rej) => {
      sqs.receiveMessage({
        QueueUrl: queueUrl,
        WaitTimeSeconds: 10,
        MaxNumberOfMessages: 1,
      }, async function (err, data) {
        try {
          if (err) rej(err);


          // If we haven't received a message in 10 seconds, resolve and let the loop restart.
          if (!data.Messages) res();

          // Guaranteed to be exactly one message in data.Messages at this point
          const deleteParams = {
            QueueUrl: queueUrl,
            ReceiptHandle: data.Messages[0].ReceiptHandle
          };

          try {
            const messageBody = JSON.parse(data.Messages[0].Body);
            await messageHandler(messageBody);
            await sqs.deleteMessage(deleteParams).promise();
          } catch (e) {

            // If delete fails, the message will be removed automatically after a minute by SQS, so
            // we don't have to worry about poison pills blocking our processing loop
            await sqs.deleteMessage(deleteParams).promise();
            rej(e)
          }

          res();

          // Last ditch error handler so we don't have unhandled promises
        } catch (e) {
          rej(e);
        }
      })
    });
  }

}

export function buildSqs(config: SqsConfig, licenceKeyData: R4ServerData): Sqs {
  switch (config.type) {
    case 'real':
      return new RealSqs(licenceKeyData);

    case 'stub':
      return new StubSqs();
  }
}
